/**
 * Meta Pixel (browser-only) para o funil público de inscrição em turmas.
 * Token da Conversions API NÃO deve existir aqui.
 * @see https://developers.facebook.com/docs/meta-pixel
 */

import { mirrorMetaEventToCapi } from '@/services/metaCapiMirror';

const PIXEL_ID = String(import.meta.env.VITE_META_PIXEL_ID || '').trim();

const trackedKeys = new Set();

function canTrack() {
    return Boolean(PIXEL_ID) && typeof window !== 'undefined' && typeof window.fbq === 'function';
}

function rememberOnce(key) {
    if (!key || typeof sessionStorage === 'undefined') {
        if (!key || trackedKeys.has(key)) return false;
        trackedKeys.add(key);
        return true;
    }

    const storageKey = `meta_px_${key}`;
    if (sessionStorage.getItem(storageKey) || trackedKeys.has(key)) {
        return false;
    }
    sessionStorage.setItem(storageKey, '1');
    trackedKeys.add(key);
    return true;
}

function sessionNonce() {
    if (typeof sessionStorage === 'undefined') {
        return String(Date.now());
    }
    const key = 'meta_px_session_nonce';
    try {
        let nonce = sessionStorage.getItem(key);
        if (!nonce) {
            nonce = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
            sessionStorage.setItem(key, nonce);
        }
        return nonce;
    } catch {
        return String(Date.now());
    }
}

/**
 * event_id de intenção (clique no submit), distinto do IC pós-API (compra_id).
 */
export function buildCheckoutClickEventId(prefix, contentId) {
    const safePrefix = String(prefix || 'initiate_checkout_turma').replace(/[^A-Za-z0-9_.:-]/g, '_').slice(0, 40);
    const safeId = String(contentId || 'unknown').replace(/[^A-Za-z0-9_.:-]/g, '_').slice(0, 64);
    return `${safePrefix}_click_${safeId}_${sessionNonce()}`;
}

/**
 * Injeta o snippet oficial e faz init. Idempotente.
 */
export function initMetaPixel() {
    if (!PIXEL_ID || typeof window === 'undefined') {
        return false;
    }

    if (typeof window.fbq === 'function') {
        return true;
    }

    /* eslint-disable */
    !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    window.fbq('init', PIXEL_ID);
    return true;
}

export function getMetaPixelId() {
    return PIXEL_ID || null;
}

export function trackPageView() {
    if (!canTrack()) return;
    window.fbq('track', 'PageView');
}

/**
 * @param {object} data
 * @param {{ eventId?: string, onceKey?: string, email?: string, phone?: string, skipMirror?: boolean }} [options]
 */
export function trackViewContent(data = {}, options = {}) {
    if (!canTrack()) return;
    if (options.onceKey && !rememberOnce(`vc_${options.onceKey}`)) return;

    const payload = { ...data };
    if (options.eventId) {
        window.fbq('track', 'ViewContent', payload, { eventID: options.eventId });
        if (!options.skipMirror) {
            mirrorMetaEventToCapi({
                eventName: 'ViewContent',
                eventId: options.eventId,
                customData: payload,
                email: options.email,
                phone: options.phone,
            });
        }
        return;
    }
    window.fbq('track', 'ViewContent', payload);
}

/**
 * @param {object} data
 * @param {{ eventId?: string, onceKey?: string, email?: string, phone?: string, skipMirror?: boolean }} [options]
 */
export function trackInitiateCheckout(data = {}, options = {}) {
    if (!canTrack()) return;
    if (options.onceKey && !rememberOnce(`ic_${options.onceKey}`)) return;

    const payload = { ...data };
    if (options.eventId) {
        window.fbq('track', 'InitiateCheckout', payload, { eventID: options.eventId });
        if (!options.skipMirror) {
            mirrorMetaEventToCapi({
                eventName: 'InitiateCheckout',
                eventId: options.eventId,
                customData: payload,
                email: options.email,
                phone: options.phone,
            });
        }
        return;
    }
    window.fbq('track', 'InitiateCheckout', payload);
}

/**
 * Disparar só após pagamento confirmado (ou inscrição gratuita).
 * @param {object} data
 * @param {{ eventId?: string, onceKey?: string }} [options]
 */
export function trackPurchase(data = {}, options = {}) {
    if (!canTrack()) return;
    if (options.onceKey && !rememberOnce(`pu_${options.onceKey}`)) return;

    const payload = { ...data };
    if (options.eventId) {
        window.fbq('track', 'Purchase', payload, { eventID: options.eventId });
        return;
    }
    window.fbq('track', 'Purchase', payload);
}

/**
 * Payload padrão de produto (turma de redação).
 */
export function buildTurmaContentPayload({
    contentId,
    contentName,
    value,
    currency = 'BRL',
} = {}) {
    const payload = {
        content_type: 'product',
        currency: currency || 'BRL',
    };

    if (contentId) {
        payload.content_ids = [String(contentId)];
    }
    if (contentName) {
        payload.content_name = String(contentName);
    }
    if (value != null && !Number.isNaN(Number(value))) {
        payload.value = Number(value);
    }

    return payload;
}

export function parseMoneyValue(raw) {
    if (raw == null || raw === '') return 0;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
    const text = String(raw).trim();
    if (!text) return 0;
    // Aceita "297", "297.00" ou "297,00"
    const normalized = text.includes(',') && text.includes('.')
        ? text.replace(/\./g, '').replace(',', '.')
        : text.replace(',', '.');
    const n = Number(normalized.replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
}
